import React from 'react';
import styled from '@emotion/styled';
import type { ThirdPartyBannerType } from 'corgi-types';
import {
  backgroundColor,
  color,
  display,
  fontSize,
  height,
  lineHeight,
  space,
  width,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import Icon from 'common/components/Icon';
import { BigHeading } from 'common/components/Text';
import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSSWithOffset } from 'common/theme/styles';
import { fill, textDecoration, textTransform } from 'common/theme/system-utilities';
import { BANNER_CONFIGS } from '../utils/config';
import { MoreInfoButton } from './MoreInfoButton';

interface ThirdPartyBannerProps {
  href?: string;
  type: ThirdPartyBannerType;
}

const Container = styled(Flex)<any>`
  ${backgroundColor};
  ${textDecoration};

  ${focusOutlineCSSWithOffset};
`;
const containerStyles = {
  as: 'a',
  flexDirection: 'column',
  alignItems: 'center',
  pb: 'sp_16',
  px: 'sp_8',
  textDecoration: 'none',
};

const Heading = styled(BigHeading)<any>`
  ${display};
  ${width};
  ${textTransform};
`;
const headingStyles = {
  as: 'span',
  display: 'block',
  width: 1,
  fontSize: ['fs_18', null, null, 'sp_24'],
  lineHeight: 'lh_22',
};

const TextWithIcon = styled(Flex)<any>`
  ${fontSize};
  ${lineHeight};
  ${color};
`;
const textWithIconStyles = {
  as: 'span',
  fontSize: 'fs_16',
};

const RightChevron = styled(Icon)<any>`
  ${width};
  ${height};
  ${fill};
`;

const BANNER_ICON_WIDTH = 45;
const BANNER_ICON_HEIGHT = 40;

const BannerIcon = styled(Icon)<any>`
  ${space};
  ${width};
  ${height};
`;
const bannerIconStyles = {
  role: 'img',
  mt: 'sp_16',
  mb: 'sp_8',
  width: BANNER_ICON_WIDTH,
  height: BANNER_ICON_HEIGHT,
};

const ThirdPartyBanner = ({ href, type }: ThirdPartyBannerProps) => {
  const { t } = useLocalization();

  const { title, link, moreInfoDrawer, icon, chevronSize, headingBottomMargin } =
    BANNER_CONFIGS.get(type);

  return (
    <Container
      data-corgi-id="third-party-banner"
      href={href || t(link)}
      target="_blank"
      rel="noopener noreferrer"
      backgroundColor="sys.secondary.background.active"
      color="sys.neutral.background.default"
      borderRadius="medium"
      {...containerStyles}
    >
      {icon && <BannerIcon name={icon} {...bannerIconStyles} />}
      {moreInfoDrawer && (
        <MoreInfoButton drawerTitle={moreInfoDrawer.title} drawerContent={moreInfoDrawer.content} />
      )}
      <Heading color="sys.neutral.background.default" mb={headingBottomMargin} {...headingStyles}>
        {t(title)}
      </Heading>
      <TextWithIcon
        color="sys.neutral.background.default"
        lineHeight={`lh_${chevronSize}`}
        {...textWithIconStyles}
      >
        <span>{t('h24_this_way')}</span>{' '}
        <RightChevron
          fill="sys.neutral.background.default"
          size={chevronSize}
          role="presentation"
          name="arrowRight"
        />
      </TextWithIcon>
    </Container>
  );
};

export default ThirdPartyBanner;
